import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { WorkViewerInfo } from '../../shared/component/headless/input/InputBody';
import { NormalSmallTextArea } from '../../shared/component/headless/textarea/TextAreaBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn } from '../../shared/component/headless/button/BtnBody';
import MoleculeInputDivForModal from '../../shared/component/molecule/MoleculeInputDivForModal';
import MoleculeTextareaDescriptionForModal from '../../shared/component/molecule/MoleculeTextareaDescriptionForModal';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { getBtnName } from '../../shared/constants/btnNames';
import { useSetting } from '../../shared/hooks/useApi/useSetting';
import { TextAlignment } from '../../shared/enum/EnumRepository';
import { SEO_DESCRIPTION_MAX_LENGTH, SEO_TITLE_MAX_LENGTH } from '../../shared/constants/limits';
import { paperTextureSmall } from '../../shared/component/paperTexture';
import { slideInFromRightWeighted } from '../../shared/component/Animation';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteIcon from '../../asset/icon/delete.png';
import { getModalMessage } from '../../shared/constants/modalMessages';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const ChangeSearchEngineModal: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { standardModal, isClosing, clearModal } = useModalStore();
  const { setting, updateSeo } = useSetting();
  const [name, setName] = useState(setting?.seo.name ?? '');
  const [nameEn, setNameEn] = useState(setting?.seo.nameEn ?? '');
  const [description, setDescription] = useState(setting?.seo.description ?? '');
  const [descriptionEn, setDescriptionEn] = useState(setting?.seo.descriptionEn ?? '');

  useEffect(() => {
    if (!setting) {
      setIsUserInput(false);
      return;
    }

    setIsUserInput(
      name !== (setting.seo.name ?? '') ||
      nameEn !== (setting.seo.nameEn ?? '') ||
      description !== setting.seo.description ||
      descriptionEn !== (setting.seo.descriptionEn ?? '')
    );
  }, [description, descriptionEn, nameEn, name, setIsUserInput, setting]);

  if (standardModal == null || setting == null) return null;

  const handleChange = async () => {
    await updateSeo({
      subTitle: setting.seo.subTitle,
      name,
      nameEn,
      description,
      descriptionEn,
      updateUploadFileReq: {
        uploadFileId: setting.seo.uploadFile.id,
        originUrl: setting.seo.uploadFile.originUrl,
        allowDownload: setting.seo.uploadFile.allowDownload,
        width: setting.seo.uploadFile.width,
        height: setting.seo.uploadFile.height,
      },
    });
  };

  return (
    <LeftModalFrame onClick={(e) => e.stopPropagation()} $isClosing={isClosing}>
      <ModalBody>
        <HeaderRow>
          <Title>{standardModal.title}</Title>
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent
            hoverOpacity
            backgroundImg={deleteIcon}
            onClick={() => clearModal(700)}
          />
        </HeaderRow>
        <OptionContainer>
          <InfoRow>
            <InfoLabel>Name</InfoLabel>
            <MoleculeInputDivForModal
              value={name}
              placeholder="Name"
              handleChange={(e) => setName(e.target.value)}
              inputStyle={WorkViewerInfo}
              alignment={TextAlignment.LEFT}
              maxLength={SEO_TITLE_MAX_LENGTH}
            />
          </InfoRow>
          <InfoRow>
            <InfoLabel>Name(EN)</InfoLabel>
            <MoleculeInputDivForModal
              value={nameEn}
              placeholder="Name(EN)"
              handleChange={(e) => setNameEn(e.target.value)}
              inputStyle={WorkViewerInfo}
              alignment={TextAlignment.LEFT}
              maxLength={SEO_TITLE_MAX_LENGTH}
            />
          </InfoRow>
          <InfoRow>
            <InfoLabel>Description</InfoLabel>
            <MoleculeTextareaDescriptionForModal
              value={description}
              placeholder={getModalMessage('SEO_DESCRIPTION_HINT')}
              handleChange={(e) => setDescription(e.target.value)}
              StyledTextarea={SeoDescriptionTextarea}
              alignment={TextAlignment.LEFT}
              maxLength={SEO_DESCRIPTION_MAX_LENGTH}
              maxRows={10}
            />
          </InfoRow>
          <InfoRow>
            <InfoLabel>Description(EN)</InfoLabel>
            <MoleculeTextareaDescriptionForModal
              value={descriptionEn}
              placeholder={getModalMessage('SEO_DESCRIPTION_EN_HINT')}
              handleChange={(e) => setDescriptionEn(e.target.value)}
              StyledTextarea={SeoDescriptionTextarea}
              alignment={TextAlignment.LEFT}
              maxLength={SEO_DESCRIPTION_MAX_LENGTH}
              maxRows={10}
            />
          </InfoRow>
        </OptionContainer>
        <BtnContainer>
          <HeadlessBtn value={getBtnName('CHANGE')} handleClick={handleChange} StyledBtn={BlackFitBtn} />
        </BtnContainer>
      </ModalBody>
    </LeftModalFrame>
  );
};

const LeftModalFrame = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  right: clamp(20px, 2vw, 30px);
  bottom: calc(8vh);
  width: 34vw;
  min-width: 480px;
  height: clamp(36vh, fit-content, 70vh);
  padding: 20px;
  box-sizing: border-box;
  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) => ($isClosing ? 'translateX(100%)' : 'translateX(0)')};
  transition: all 0.6s ease-out;
  ${paperTextureSmall}
  ${slideInFromRightWeighted}
`;

const ModalBody = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: clamp(10px, 4vh, 24px);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 1.5vh;
  flex-shrink: 0;
`;

const Title = styled.div`
  min-width: 0;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const InfoRow = styled.div`
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const InfoLabel = styled.span`
  min-width: 100px;
  padding: 4px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const SeoDescriptionTextarea = styled(NormalSmallTextArea)`
  min-height: 60px;
  margin-top: 4px;
  ${({ theme }) => theme.typography.Body_04};
`;

const BtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 10px 0px;
`;

export default ChangeSearchEngineModal;
